import React, { useState, useEffect } from 'react';
import { 
  X, 
  Plus, 
  Trash2, 
  Save, 
  AlertCircle,
  Globe,
  List,
  Settings,
  Info
} from 'lucide-react';
import { 
  doc, 
  setDoc,
  getDoc,
  Timestamp
} from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface GlobalAttribute {
  name: string;
  type: 'text' | 'number' | 'select' | 'multiselect' | 'boolean';
  required: boolean;
  options?: string[];
  description?: string;
  order?: number;
}

interface GlobalAttributesModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
}

const GlobalAttributesModal: React.FC<GlobalAttributesModalProps> = ({
  isOpen,
  onClose,
  clientId
}) => {
  const [attributes, setAttributes] = useState<GlobalAttribute[]>([]);
  const [newAttribute, setNewAttribute] = useState<GlobalAttribute>({
    name: '',
    type: 'text',
    required: false,
    options: [],
    description: ''
  });
  const [showNewForm, setShowNewForm] = useState(false);
  const [optionInput, setOptionInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cargar atributos globales desde Firestore
  useEffect(() => {
    if (!isOpen || !clientId) return;

    const loadGlobalAttributes = async () => {
      try {
        setLoading(true);
        const docRef = doc(db, 'clients', clientId, 'catalogRules', 'globalAttributes');
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAttributes(data.attributes || []);
        } else {
          setAttributes([]);
        }
      } catch (error) {
        console.error('Error loading global attributes:', error);
        setError('Error al cargar los atributos globales');
      } finally {
        setLoading(false);
      }
    };

    loadGlobalAttributes();
  }, [isOpen, clientId]);

  // Guardar atributos globales en Firestore
  const saveAttributes = async () => {
    setSaving(true);
    setError(null);

    try {
      const docRef = doc(db, 'clients', clientId, 'catalogRules', 'globalAttributes');
      await setDoc(docRef, {
        attributes: attributes,
        updatedAt: Timestamp.now()
      });
      
    } catch (error) {
      console.error('Error saving global attributes:', error);
      setError('Error al guardar los atributos globales');
    } finally {
      setSaving(false);
    }
  };

  // Agregar nuevo atributo
  const addAttribute = () => {
    if (!newAttribute.name.trim()) {
      setError('El nombre del atributo es obligatorio');
      return;
    }

    // Verificar que no exista un atributo con el mismo nombre
    if (attributes.some(attr => attr.name.toLowerCase() === newAttribute.name.toLowerCase())) {
      setError('Ya existe un atributo global con este nombre');
      return;
    }

    const updatedAttributes = [...attributes, { 
      ...newAttribute, 
      order: attributes.length 
    }];
    setAttributes(updatedAttributes);
    
    // Reset form
    setNewAttribute({
      name: '',
      type: 'text',
      required: false,
      options: [],
      description: ''
    });
    setOptionInput('');
    setShowNewForm(false);
    setError(null);
  };

  // Eliminar atributo
  const deleteAttribute = (index: number) => {
    const updatedAttributes = attributes.filter((_, i) => i !== index);
    setAttributes(updatedAttributes);
  };

  // Agregar opción a select/multiselect
  const addOption = () => {
    if (!optionInput.trim()) return;
    
    const options = newAttribute.options || [];
    if (!options.includes(optionInput.trim())) {
      setNewAttribute({
        ...newAttribute,
        options: [...options, optionInput.trim()]
      });
    }
    setOptionInput('');
  };

  // Eliminar opción
  const removeOption = (option: string) => {
    setNewAttribute({
      ...newAttribute,
      options: (newAttribute.options || []).filter(o => o !== option)
    });
  };

  // Atributos sugeridos comunes
  const suggestedAttributes = [
    { name: 'Color', type: 'text' as const, description: 'Color principal del producto' },
    { name: 'Talle', type: 'select' as const, description: 'Talle o tamaño', options: ['XS', 'S', 'M', 'L', 'XL', 'XXL'] },
    { name: 'Material', type: 'text' as const, description: 'Material principal' },
    { name: 'Línea de Producto', type: 'text' as const, description: 'Línea o colección del producto' },
    { name: 'Temporada', type: 'select' as const, description: 'Temporada del producto', options: ['Primavera/Verano', 'Otoño/Invierno', 'Todo el año'] },
    { name: 'Género', type: 'select' as const, description: 'Género objetivo', options: ['Masculino', 'Femenino', 'Unisex', 'Niños', 'Niñas'] },
    { name: 'Peso', type: 'number' as const, description: 'Peso del producto en gramos' },
    { name: 'Origen', type: 'text' as const, description: 'País de origen' },
    { name: 'En Oferta', type: 'boolean' as const, description: 'Si el producto está en oferta' },
    { name: 'Stock Disponible', type: 'boolean' as const, description: 'Si hay stock disponible' }
  ];

  // Agregar atributo sugerido
  const addSuggestedAttribute = (suggested: typeof suggestedAttributes[0]) => {
    if (attributes.some(attr => attr.name.toLowerCase() === suggested.name.toLowerCase())) {
      setError(`El atributo "${suggested.name}" ya existe`);
      return;
    }

    setAttributes([...attributes, {
      name: suggested.name,
      type: suggested.type,
      required: false,
      options: suggested.options || [],
      description: suggested.description,
      order: attributes.length
    }]);
    setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Globe className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Atributos Globales</h2>
              <p className="text-sm text-gray-600">Atributos comunes a todas las categorías</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Cargando atributos...</p>
            </div>
          ) : (
            <>
              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">¿Qué son los atributos globales?</p>
                  <p>Los atributos globales se aplican automáticamente a todas las categorías. Son útiles para propiedades comunes como color, talle, material, etc. Esto evita tener que definir los mismos atributos en cada categoría.</p>
                </div>
              </div>

              {/* Lista de atributos existentes */}
              {attributes.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                    <List className="w-5 h-5 text-purple-600" />
                    Atributos Globales Configurados ({attributes.length})
                  </h3>
                  <div className="space-y-2">
                    {attributes.map((attr, index) => (
                      <div
                        key={index}
                        className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-semibold text-gray-900">{attr.name}</span>
                              <span className="px-2 py-1 text-xs rounded-full bg-purple-100 text-purple-700">
                                {attr.type}
                              </span>
                              {attr.required && (
                                <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700">
                                  Obligatorio
                                </span>
                              )}
                              <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                                Global
                              </span>
                            </div>
                            {attr.description && (
                              <p className="text-sm text-gray-600 mb-2">{attr.description}</p>
                            )}
                            {attr.options && attr.options.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {attr.options.map((option, optIdx) => (
                                  <span
                                    key={optIdx}
                                    className="px-2 py-1 text-xs bg-gray-100 rounded-md"
                                  >
                                    {option}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => deleteAttribute(index)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors ml-4"
                            title="Eliminar atributo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Atributos sugeridos */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  <Settings className="w-5 h-5 text-purple-600" />
                  Atributos Sugeridos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {suggestedAttributes
                    .filter(suggested => !attributes.some(attr => 
                      attr.name.toLowerCase() === suggested.name.toLowerCase()
                    ))
                    .map((suggested, index) => (
                      <button
                        key={index}
                        onClick={() => addSuggestedAttribute(suggested)}
                        className="text-left p-3 border border-gray-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-colors group"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700 group-hover:text-purple-700">
                            {suggested.name}
                          </span>
                          <Plus className="w-4 h-4 text-gray-400 group-hover:text-purple-600" />
                        </div>
                        <span className="text-xs text-gray-500">{suggested.type}</span>
                      </button>
                    ))}
                </div>
              </div>

              {/* Formulario para nuevo atributo */}
              {showNewForm ? (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-purple-600" />
                    Nuevo Atributo Global
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre del atributo *
                      </label>
                      <input
                        type="text"
                        value={newAttribute.name}
                        onChange={(e) => setNewAttribute({ ...newAttribute, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Ej: Color, Talle, Material"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Descripción
                      </label>
                      <input
                        type="text"
                        value={newAttribute.description}
                        onChange={(e) => setNewAttribute({ ...newAttribute, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        placeholder="Descripción del atributo (opcional)"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Tipo
                        </label>
                        <select
                          value={newAttribute.type}
                          onChange={(e) => setNewAttribute({ 
                            ...newAttribute, 
                            type: e.target.value as GlobalAttribute['type'],
                            options: (e.target.value === 'select' || e.target.value === 'multiselect') ? [] : undefined
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="text">Texto</option>
                          <option value="number">Número</option>
                          <option value="select">Selección única</option>
                          <option value="multiselect">Selección múltiple</option>
                          <option value="boolean">Sí/No</option>
                        </select>
                      </div>

                      <div className="flex items-center">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newAttribute.required}
                            onChange={(e) => setNewAttribute({ ...newAttribute, required: e.target.checked })}
                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                          />
                          <span className="text-sm font-medium text-gray-700">Obligatorio</span>
                        </label>
                      </div>
                    </div>

                    {/* Opciones para select/multiselect */}
                    {(newAttribute.type === 'select' || newAttribute.type === 'multiselect') && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Opciones
                        </label>
                        <div className="flex gap-2 mb-2">
                          <input
                            type="text"
                            value={optionInput}
                            onChange={(e) => setOptionInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addOption())}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                            placeholder="Agregar opción"
                          />
                          <button
                            onClick={addOption}
                            className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        {newAttribute.options && newAttribute.options.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {newAttribute.options.map((option, idx) => (
                              <span
                                key={idx}
                                className="px-2 py-1 bg-white border border-gray-300 rounded-md flex items-center gap-1"
                              >
                                {option}
                                <button
                                  onClick={() => removeOption(option)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={addAttribute}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Agregar
                      </button>
                      <button
                        onClick={() => {
                          setShowNewForm(false);
                          setNewAttribute({
                            name: '',
                            type: 'text',
                            required: false,
                            options: [],
                            description: ''
                          });
                          setOptionInput('');
                          setError(null);
                        }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowNewForm(true)}
                  className="w-full py-3 border-2 border-dashed border-purple-300 rounded-lg text-purple-600 hover:border-purple-400 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Agregar Atributo Personalizado
                </button>
              )}

              {/* Error message */}
              {error && (
                <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {attributes.length} atributo{attributes.length !== 1 ? 's' : ''} global{attributes.length !== 1 ? 'es' : ''} configurado{attributes.length !== 1 ? 's' : ''}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={async () => {
                await saveAttributes();
                onClose();
              }}
              disabled={saving}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GlobalAttributesModal;